import {
  Box,
  Grid,
  Typography,
  useTheme,
  Tooltip,
  Link,
  IconButton,
} from "@mui/material";

import {
  Chip,
} from "@mui/material";
import { CLAIM_TIMELINE_ENTRIES, ORDER_AVAILABILITY } from '../../../constants/applicationConstants';
import Header from "../../common/Header";
import Button from "../../common/Button";
import Card from "../../common/Card";
import {
  OrderInfo,
  getOrderItemsByOrderId,
  TissueTrackingDetail,
  useOrderSlice,
  OrderItemDTO,
  fetchOrderDocumentById,
  orderItemInvoice,
  fetchOrderDocumentsByIds,
  getOrderTrackingLogs,
  previewPackagePDF,
  previewSalesPDF,
  previewPrescriptionPDF,
  previewProofOfDelivery,
  previewEvaltionForm,
  updateOrder,
  OrderFulfillmentInfo,
} from "../../../redux/slice/order/OrderSlice";
import { useForm } from "react-hook-form";
import {
  ArrowBack,
  AssignmentOutlined,
  CommentOutlined,
  NoteAddOutlined,
  PictureAsPdfOutlined,
  WallpaperOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import CustomCheckbox from "../../common/CustomCheckBox";
import { tokens } from "../../../theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import dayjs from "dayjs";
import CustomIconButton from "../../common/CustomIconButton";
import { setSingleAlertObj, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import CustomTextField from "../../common/CustomTextField";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { CAN_ACCESS_APPLICATION, PRODUCT_CATEGORY, ORDER_DETAILS_BUTTONS, PRODUCT_STATUS } from "../../../constants/applicationConstants";
import { Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomCommentPopup from "../../common/CustomCommentPopup";
import { formatShippingTrackingDateTime, formatShippingTrackingExpectedDateTime, priceFormatter } from "../../../utils/Utility";
import { getPatient } from "../../../redux/slice/patient/patientSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import ShipmentInfoPopup from "../orders/ShipmentInfoPopup";
import TimelineView from "../../common/TimelineView";
import { fetchClaimById } from "../../../redux/slice/claim/ClaimSlice";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

type TimelineEntry = {
  text: string;
  time: string;
  button: boolean;
};

type TimelineDataItem = {
  date: string;
  entries: TimelineEntry;
};

interface Row {
  select: any;
  orderedGrafts: string;
  serialNumber: string;
  id: number;
  tissueTrackingId: number;
  productReturnId: number;
  productReturnStatus: string;
  orderStatus: string;
  comment: string;
  tissueTrackingDocumentId: number;
  tissueTrackingInfo: {
    barcodes: number[];
    imagesOfWounds: number[];
    medicalNotes: number[];
  };
  returnDocumentId: number;
  invoice: orderItemInvoice;
  invoiceId: number;
  uploadedProductPackageIds: number[];
  appRetDate: string | JSX.Element;
  invoiceDate: string | JSX.Element;
  claimId?: String;
}

const ClaimDetails = () => {
  const { control, setValue, getValues } = useForm<TissueTrackingDetail>({
    defaultValues: {
      patient: "",
      product: "",
      order: "",
      po: "",
      dateOfService: "",
      shippingCarrier: "",
      trackingNumber: "",
      orderStatus: "",
      providerName: "",
      practiceName: "",
    },
  });

  const { navigate } = useNavigation();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const [order, setOrder] = useState<OrderInfo>();
  const { orderItemsListInfo, orderDocumentInfo, orderTrackingLogs, claimInfo } = useOrderSlice();
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [commentData, setCommentData] = useState<{ title: string; body: string }>({
    title: "",
    body: "",
  });

  const [showCommentAlert, setShowCommentAlert] = useState(false);
  const [comment, setComment] = useState("");
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    productName: string;
    fileName: string;
  }>({ isShow: false, productName: "", fileName: "" });
  const [mfrPatientId, setMfrPatientId] = useState<string>("");

  const [shipmentInfoOpen, setShipmentInfoOpen] = useState(false);

  const parsedTrackingInfo = orderTrackingLogs?.detailedTrackingInfo
    ? JSON.parse(orderTrackingLogs.detailedTrackingInfo)
    : null;

  const trackingData = parsedTrackingInfo?.track_info;
  const scanEvents = trackingData?.tracking?.providers[0]?.events || [];
  const dateAndTimes = trackingData?.milestone || [];
  const shippingDateRaw = dateAndTimes.find((d: any) => d.key_stage === "InfoReceived")?.time_iso;
  const departureTimeRaw = dateAndTimes.find((d: any) => d.key_stage === "PickedUp")?.time_iso;
  const timeMetrics = trackingData?.time_metrics;
  const shippingDate = formatShippingTrackingDateTime(shippingDateRaw);
  const departureTime = formatShippingTrackingDateTime(departureTimeRaw);
  const expectedArrivalFrom = formatShippingTrackingDateTime(timeMetrics?.estimated_delivery_date?.from);
  const expectedArrivalTo = formatShippingTrackingDateTime(timeMetrics?.estimated_delivery_date?.to);
  const deliveryEvent = scanEvents.find((event: any) => event.stage === "Delivered");
  const actualDeliveryTime = formatShippingTrackingDateTime(deliveryEvent?.time_iso);
  const canAccessSalesInvoice = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderDetailsActionButton", ORDER_DETAILS_BUTTONS.SALES_INVOICE)(state));

  const canAccessApplication = useSelector((state: RootState) =>
    selectIsValueFoundInToken(
      "canAccessApplication",
      CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT
    )(state)
  );

  const canViewCOGSPrice = useSelector((state: RootState) => selectIsValueFoundInToken("canViewCOGSPrice", CAN_ACCESS_APPLICATION.ORDER_MANAGEMENT)(state));

  const isNotPlacedOrCanceled = !(order?.orderStatus === PRODUCT_STATUS.PLACED || order?.orderStatus === PRODUCT_STATUS.CANCELED);
  const isProcessing = order?.orderStatus === PRODUCT_STATUS.PROCESSING;
  const isShipped = order?.orderStatus === PRODUCT_STATUS.SHIPPED;
  const isDelivered = order?.orderStatus === PRODUCT_STATUS.DELIVERED;

  const [isEditing, setIsEditing] = useState(false);
  const [poNumberValue, setPoNumberValue] = useState<string>(order?.poNumber || getValues("po") || "");
  const [documentsMenuAnchor, setDocumentsMenuAnchor] = useState<null | HTMLElement>(null);
  const [currClaimId, setCurrClaimId] = useState<string>();
  const [ timelineClaimData, setTimelineClaimData ] = useState<TimelineDataItem[]>();

  const statusPriority: Record<string, number> = {
  [CLAIM_TIMELINE_ENTRIES.SUBMITTED] : 1,
  [CLAIM_TIMELINE_ENTRIES.DENIED]: 2,
  [CLAIM_TIMELINE_ENTRIES.APPEALED]: 3,
  [CLAIM_TIMELINE_ENTRIES.REIMBURSED]: 4,
};

  // Mounting
  useEffect(() => {
    const orderDetails: OrderInfo = location?.state?.orderDetails;
    setOrder(orderDetails);

    if (orderDetails) {
      console.log("order details: ", orderDetails)
      setCurrClaimId(orderDetails.claimId)
      setInitialValues(orderDetails);
      dispatch(getOrderItemsByOrderId(orderDetails.orderId));
      dispatch(getOrderTrackingLogs({ orderId: orderDetails.orderId, trackingNumber: orderDetails.trackingNumber }));
    }
    const fetchPatientDetails = async () => {
      try {
        const response = await dispatch(getPatient({ patientId: orderDetails.patientId.toString() })).unwrap();
        const updateOrderInfo = response.data.patientInfo?.find((info: { infoType: string; }) => info.infoType === "MANUFACTURER_PATIENT_IDS");

        if (updateOrderInfo && updateOrderInfo.patientinfoData) {
          const manufacturerId = orderDetails.manufacturerId;

          const manufacturerEntries = Object.values(updateOrderInfo.patientinfoData) as {
            manufacturerId: number;
            manufacturerPatientId: string;
          }[];

          const manufacturerEntry = manufacturerEntries.find(
            (entry) => entry.manufacturerId === manufacturerId
          );

          if (manufacturerEntry) {
            orderDetails.manufacturerPatientId = manufacturerEntry.manufacturerPatientId;
            setMfrPatientId(manufacturerEntry.manufacturerPatientId);
          }
        }

      } catch (error) {
        console.error("Error fetching patient details:", error);
      }
    };

    fetchPatientDetails();
  }, [location?.state?.orderDetails, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: { key: string }) => {
      if (event.key === "Escape") {
        setShowCommentAlert(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const setInitialValues = (orderDetails: OrderInfo) => {
    setValue("order", orderDetails.orderNumber);
    if (orderDetails.patientId) {
      setValue("patient", orderDetails.patientName);
    }
    setValue("product", orderDetails.productName);
    setValue("po", orderDetails.poNumber);
    setValue(
      "dateOfService",
      dayjs(orderDetails.dateOfService).format("MM/DD/YYYY")
    );
    setValue("shippingCarrier", orderDetails.shippingCarrier);
    setValue("trackingNumber", orderDetails.trackingNumber);
    setValue("orderStatus", orderDetails.orderStatus);
    setValue(
      "providerName",
      orderDetails.providerName || orderDetails?.orderInfo?.provider
    );
    setValue("practiceName", orderDetails.practiceName);
  };

  const getComment = (item: OrderItemDTO) => {
    if (item.tissueTrackingInfo && item.tissueTrackingInfo.comment) {
      return item.tissueTrackingInfo.comment;
    } else if (item?.returnTrackingInfo?.additionalContext) {
      return item.returnTrackingInfo.additionalContext;
    }
    return "";
  };

  const handlePreviewAction = async (row: Row) => {
    let documentId = row.tissueTrackingDocumentId
      ? row.tissueTrackingDocumentId
      : row.returnDocumentId;
    const actionResult = await dispatch(fetchOrderDocumentById(documentId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName:
          order?.productName +
          (orderItemStatus(row) === `Returned (#${row.productReturnId})`
            ? ` - RETURN`
            : ` - TISSUE TRACKING`),
        fileName:
          orderItemStatus(row) === `Returned (#${row.productReturnId})`
            ? `RETURN_${order?.orderNumber}`
            : `TISSUE_TRACKING_${order?.orderNumber}`,
      });
    }

    handleMenuClose(row.id.toString());
  };

  const handleImageViewAction = async (row: Row) => {
    let documentIds =
      row.tissueTrackingInfo?.imagesOfWounds?.length > 0
        ? row.tissueTrackingInfo.imagesOfWounds
        : row.uploadedProductPackageIds?.length > 0
          ? row.uploadedProductPackageIds
          : [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName:
          row.uploadedProductPackageIds?.length > 0
            ? order?.productName + " - PRODUCT PACKAGING IMAGES"
            : order?.productName +
            (orderItemStatus(row) === `Returned (#${row.productReturnId})`
              ? ` - RETURN`
              : ` - TISSUE TRACKING`),
        fileName:
          row.uploadedProductPackageIds?.length > 0
            ? `PRODUCT_PACKAGING_IMAGES_${order?.orderNumber}`
            : orderItemStatus(row) === `Returned (#${row.productReturnId})`
              ? `RETURN_${order?.orderNumber}`
              : `TISSUE_TRACKING_${order?.orderNumber}`,
      });
    }
    handleMenuClose(row.id.toString());
  };

  const handleViewMedicalNotesAction = async (row: Row) => {
    let documentIds = row.tissueTrackingInfo?.medicalNotes ?? [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: order?.productName + " - TISSUE TRACKING",
        fileName: `TISSUE_TRACKING_MEDICAL_NOTES_${order?.orderNumber}`,
      });
    }
    handleMenuClose(row.id.toString());
  };

  const dates = (row: OrderItemDTO) => {
    if (row.tissueTrackingId && row.appliedDate) {
      return dayjs(row.appliedDate).format("MM/DD/YYYY");
    } else if (row.productReturnId && row.returnedDate) {
      return dayjs(row.returnedDate).format("MM/DD/YYYY");
    } else {
      return "-";
    }
  };

  const orderItemRow: Row[] = useMemo(() => {
    let orderItems: Row[] = [];
    if (orderItemsListInfo) {
      orderItems = orderItemsListInfo
        .map((item: OrderItemDTO) => {
          return {
            select: <CustomCheckbox control={control} name="select" />,
            orderedGrafts: item.size,
            serialNumber: item.serialNumber,
            id: item.orderItemId,
            tissueTrackingId: item.tissueTrackingId,
            productReturnId: item.productReturnId,
            productReturnStatus: item.productReturnStatus,
            orderStatus: location?.state?.orderDetails?.orderStatus,
            comment: getComment(item),
            tissueTrackingDocumentId: item.tissueTrackingDocumentId,
            tissueTrackingInfo: {
              barcodes: item.tissueTrackingInfo?.barcodes || [],
              imagesOfWounds: item.tissueTrackingInfo?.imagesOfWounds || [],
              medicalNotes: item.tissueTrackingInfo?.medicalNotes || [],
            },
            returnDocumentId: item.returnDocumentId,
            invoice: item.invoice,
            invoiceId: item.invoiceId,
            uploadedProductPackageIds:
              item.returnTrackingInfo?.productBoxPictures || [],
            appRetDate: dates(item) || "N/A",
            invoiceDate: item.invoicedDate
              ? dayjs(item.invoicedDate).format("MM/DD/YYYY")
              : "-",
            units: item.units,
            extendedPrice: item.extendedPrice,
            invoiceNumber: item.mfgInvoiceNumber,
            quantity: item.quantity,
            hasSKUPrice: item.hasSKUPrice,
            productItemDescription: item.productItemDescription,
            claimId: item.claimId,
            claimNumber: item.claimNumber
          };
        })
        .sort((a, b) => a.units - b.units);
    }
    return orderItems;
  }, [orderItemsListInfo, control, location?.state?.orderDetails?.orderStatus]);

  const claimedItems = useMemo(
    () => {
      if (currClaimId == null) {
        return orderItemRow;
      }
      return orderItemRow.filter(row => row.claimId === currClaimId);
    },
    [orderItemRow, currClaimId]
  );


  const otherItems = useMemo(
    () => orderItemRow.filter(row => row.claimId !== currClaimId),
    [orderItemRow, currClaimId]
  );

  useEffect(() => {
    if(currClaimId!=null){
      dispatch(fetchClaimById(Number(currClaimId)))
    }
  }, [currClaimId])

  useEffect(() => {

    if (!claimInfo) return;

    const timeline: TimelineDataItem[] = [];

    if (claimInfo.submittedDate) {
      timeline.push({
        date: claimInfo.submittedDate,
        entries: {
          text: CLAIM_TIMELINE_ENTRIES.SUBMITTED,
          time: claimInfo.submittedDate,
          button: false
        }
      });
    }
    if (claimInfo.appealedDate) {
      timeline.push({
        date: claimInfo.appealedDate,
        entries: {
          text: CLAIM_TIMELINE_ENTRIES.APPEALED,
          time: claimInfo.appealedDate,
          button: false
        }
      });
    }
    if (claimInfo.deniedDate) {
      timeline.push({
        date: claimInfo.deniedDate,
        entries: {
          text: CLAIM_TIMELINE_ENTRIES.DENIED,
          time: claimInfo.deniedDate,
          button: false
        }
      });
    }
    if (claimInfo.reimbursedDate) {
      timeline.push({
        date: claimInfo.reimbursedDate,
        entries: {
          text: CLAIM_TIMELINE_ENTRIES.REIMBURSED,
          time: claimInfo.reimbursedDate,
          button: false
        }
      });
    }

    timeline.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      const priorityA = statusPriority[a.entries.text] || 99;
      const priorityB = statusPriority[b.entries.text] || 99;
      return priorityA - priorityB;
    });
    setTimelineClaimData(timeline);

  }, [claimInfo])

  const orderItemStatus = useCallback((row: Row) => {
    if (row?.tissueTrackingId) {
      return `Applied (#${row.tissueTrackingId})`;
    } else if (row?.productReturnId) {
      // return `Returned (#${row.productReturnId})`;
      //CS-1164 START
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        return  `Return Initiated (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        return  `Return Pending (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        return `Returned (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        // return `Return Rejected (#${row.productReturnId})`;
        return `Available (Return Rejected)`;
      }
      //CS-1164 END
    } else if (row?.invoiceId) {
      return "Available";
    }
    return "Pending";
  }, []);

  const detailsColumns = [
    { id: "orderedGrafts", label: "Ordered Size" },
    { id: "serialNumber", label: "Serial Number" },
    { id: "status", label: "Status" },
    { id: "appRetDate", label: "App/Ret Date" },
    { id: "invoiceDate", label: "Invoice" },
    { id: "action", label: "Action" },
  ];

  useEffect(() => {
    console.log("order: ", order)
  }, [order])
  const [menuAnchor, setMenuAnchor] = useState<{
    [key: string]: null | HTMLElement;
  }>({});

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string
  ) => {
    setMenuAnchor((prev) => ({ ...prev, [rowId]: event.currentTarget }));
  };

  const handleMenuClose = (rowId: string) => {
    setMenuAnchor((prev) => ({ ...prev, [rowId]: null }));
  };

  const showCustomCommentPopup = (row: any) => {
    const title = `Comment ${row.tissueTrackingId ? "Record product usage" : "Return product"}`;
    const body = row.comment || "";
    setCommentData({ title, body });
    setShowCommentPopup(true);
    handleMenuClose(row.id.toString());
  };
  const showCustomCommentPopupFromOrderForm = (row: any) => {
    const title = "Additional Notes / Comments";
    const body = row.additionalNotes;
    setCommentData({ title, body });
    setShowCommentPopup(true);
  };

  const hideCustomCommentPopup = () => {
    setShowCommentPopup(false);
  }

  const handlePDFPreviewAction = async (row: OrderInfo) => {
    const documentId = row.orderDocumentId ? row.orderDocumentId : 0;
    const actionResult = await dispatch(fetchOrderDocumentById(documentId));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - ORDER`,
        fileName: row.orderNumber,
      });
    }
  };

  const handlePackagePreview = async (row: OrderInfo) => {
    const orderId = row.orderId;
    const actionResult = await dispatch(previewPackagePDF(orderId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - Packing List`,
        fileName: row.orderNumber + "_PackingList",
      });
    }
  };

  const handleSalesInvoicePreviw = async (row: OrderInfo) => {
    const orderId = row.orderId;
    const actionResult = await dispatch(previewSalesPDF(orderId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - Sales Invoice`,
        fileName: row.orderNumber + "_SalesInvoice",
      });
    }
  };

  const handlePrescriptionPrview = async (row: OrderInfo) => {
    const orderId = row.orderId;
    const actionResult = await dispatch(previewPrescriptionPDF(orderId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - Prescription`,
        fileName: row.orderNumber + "_Prescription",
      });
    }
  };

  const handleCMSPrview = async (row: OrderInfo) => {
    const orderId = row.orderId;
    console.log("row ", row)
    if (row.cmsDocumentId) {
      const actionResult = await dispatch(fetchOrderDocumentsByIds([row.cmsDocumentId]));
      const documentData = actionResult.payload;

      if (documentData && documentData.content) {
        setShowPDF({
          isShow: true,
          productName: row.productName + ` - CMS 1500 Form`,
          fileName: row.orderNumber + "_CMS1500Form",
        });
      }
    }
  };

  const handleProofOfDeliveryPreviw = async (row: OrderInfo) => {
    const orderId = row.orderId;
    const actionResult = await dispatch(previewProofOfDelivery(orderId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - Proof Of Delivery`,
        fileName: row.orderNumber + "_ProofOfDelivery",
      });
    }
  };

  const handleAssessmentForm = async (row: OrderInfo) => {
    const orderId = row.orderId;
    const actionResult = await dispatch(previewEvaltionForm(orderId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row.productName + ` - Assessment Form`,
        fileName: row.orderNumber + "_AssessmentForm",
      });
    }
  };
  //

  const mapOrderItemRow = (row: any) => {
    const hasMenuItems =
      row.tissueTrackingDocumentId ||
      row.returnDocumentId ||
      row.tissueTrackingInfo.imagesOfWounds?.length ||
      row.tissueTrackingInfo.medicalNotes?.length ||
      row.uploadedProductPackageIds?.length ||
      row.comment;

    return {
      ...row,
      status: orderItemStatus(row),
      action: hasMenuItems ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            onClick={(event) => handleMenuClick(event, row.id.toString())}
            sx={{ mt: -1 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor[row.id]}
            open={Boolean(menuAnchor[row.id])}
            onClose={() => handleMenuClose(row.id.toString())}
          >
            {row.tissueTrackingDocumentId || row.returnDocumentId ? (
              <MenuItem onClick={() => handlePreviewAction(row)}>
                <CustomIconButton
                  tooltipPlacement="top"
                  color="info"
                  size="small"
                  variant="outlined"
                >
                  <AssignmentOutlined fontSize="small" />
                </CustomIconButton>
                <Typography ml={1}>Document</Typography>
              </MenuItem>
            ) : null}

            {row.tissueTrackingInfo.imagesOfWounds?.length ||
              row.uploadedProductPackageIds?.length ? (
              <MenuItem onClick={() => handleImageViewAction(row)}>
                <CustomIconButton
                  tooltipPlacement="top"
                  color="info"
                  size="small"
                  variant="outlined"
                >
                  <WallpaperOutlined fontSize="small" />
                </CustomIconButton>
                <Typography ml={1}>Image</Typography>
              </MenuItem>
            ) : null}

            {row.tissueTrackingId && row.tissueTrackingInfo.medicalNotes?.length ? (
              <MenuItem onClick={() => handleViewMedicalNotesAction(row)}>
                <CustomIconButton
                  tooltipPlacement="top"
                  color="info"
                  size="small"
                  variant="outlined"
                >
                  <NoteAddOutlined fontSize="small" />
                </CustomIconButton>
                <Typography ml={1}>Medical Note</Typography>
              </MenuItem>
            ) : null}

            {row.comment ? (
              <MenuItem onClick={() => showCustomCommentPopup(row)}>
                <CustomIconButton
                  tooltipPlacement="top"
                  color="info"
                  size="small"
                  variant="outlined"
                >
                  <CommentOutlined fontSize="small" />
                </CustomIconButton>
                <Typography ml={1}>
                  Comment{" "}
                  {row.tissueTrackingId
                    ? "Record product usage"
                    : "Return product"}
                </Typography>
              </MenuItem>
            ) : null}
          </Menu>
        </Box>
      ) : null,
      invoiceDate: (
        <Box display="flex" alignItems="center" gap={1}>
          {row.invoiceNumber && canAccessApplication ? (
            <>
              <Typography sx={{ color: "#42A5F5", cursor: "pointer" }}>
                <Link
                  component="button"
                  underline="hover"
                  variant="body2"
                  onClick={() =>
                    navigate("/invoice_list", {
                      state: { orderNumber: order?.orderNumber },
                    })
                  }
                  color={colors.info[300]}
                >
                  {row.invoiceNumber}
                </Link>
              </Typography>
              <Typography>
                {row.invoiceDate && <span>{row.invoiceDate}</span>}
              </Typography>
            </>
          ) : (
            <Typography>
              {row.invoiceNumber && row.invoiceDate
                ? `${row.invoiceNumber} ${row.invoiceDate}`
                : "-"}
            </Typography>
          )}
        </Box>
      ),
    };
  };

  const claimedDetailsRows = claimedItems?.map(mapOrderItemRow);
  const otherDetailsRows = otherItems?.map(mapOrderItemRow);


  const handleShipmentTracking = (order: OrderInfo) => {
    setShipmentInfoOpen(true);
    // const carrierUrls: { [key: string]: string } = {
    //   FEDEX: `https://fedex.com/fedextrack/?trknbr=${order.trackingNumber}`,
    //   USPS: `https://tools.usps.com/go/TrackConfirmAction?tLc=3&text28777=&tLabels=${order.trackingNumber}%2C%2C`,
    //   DHL: `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${order.trackingNumber}&submit=1&inputsource=marketingstage`,
    //   UPS: `https://www.ups.com/track?tracknum=${order.trackingNumber}&loc=en_US&requester=QUIC/`,
    // };

    // if (order.trackingNumber && order.shippingCarrier in carrierUrls) {
    //   window.open(carrierUrls[order.shippingCarrier], "_blank");
    // } else {
    //   console.warn("Unsupported carrier or missing tracking number", order);
    // }
  };
  const totalAmount = claimedDetailsRows?.reduce((sum: any, row: any) => {
    return sum + Number(row?.quantity * row.extendedPrice);
  }, 0);

  const totalPrice = claimedDetailsRows
    .filter((row) => !(row.productReturnId && row.productReturnStatus === ORDER_AVAILABILITY.RETURNED))//CS-1164
    .reduce((sum, row) => {
      if (row.extendedPrice != null && row.units != null) {
        return sum + (row.hasSKUPrice ? row.extendedPrice : row.extendedPrice * row.units);
      }
      return sum;
    }, 0);

  const orderObj: OrderFulfillmentInfo = {
    orderId: order?.orderId || 0,
    practiceId: order?.practiceId || 0,
    ivrId: order?.ivrId || 0,
    patientId: order?.patientId || 0,
    practiceName: order?.practiceName || "",
    manufacturerName: order?.manufacturerName || "",
    poNumber: order?.poNumber || "",
    orderStatusToUpdate: order?.orderStatus || "",
    updateStatusDate: new Date().toISOString(),
    dateOfService: order?.dateOfService || '',
    patientName: order?.patientName || "",
    productName: order?.productName || "",
    providerName: order?.providerName || "",
    orderNumber: order?.orderNumber || "",
    orderDate: order?.orderDate || "",
    orderType: order?.orderType || "",
    orderStatus: order?.orderStatus || "",
    mfgConfirmationNumber: order?.mfgConfirmationNumber || "",
    shippedDate: order?.shippedDate || "",
    deliveredDate: order?.deliveredDate || "",
    processedDate: order?.processedDate || "",
    shippingCarrier: order?.shippingCarrier || "",
    trackingNumber: order?.trackingNumber || "",
    orderItems: orderItemsListInfo || [],
    manufacturerOrderId: order?.manufacturerOrderId || "",
    manufacturerPatientId: order?.manufacturerPatientId || "",
  };

  const handleEditClick = () => {
    setPoNumberValue(order?.poNumber || getValues("po") || "");
    setIsEditing(true);
  };

  const renderPoNumberComponent = () => {
    return (
      <CustomTextField
        controllerName="po"
        autoFocus
        control={control}
        value={poNumberValue}
        onChange={(event) => {
          setPoNumberValue(event.target.value);
        }}
        onBlur={async () => {
          setIsEditing(false);
          const newValue = poNumberValue.trim();

          if ((newValue && newValue !== getValues("po")) || (newValue !== order?.poNumber)) {
            setValue("po", newValue);

            const updatePoNumber = {
              ...orderObj,
              poNumber: newValue,
            };

            try {
              const response = await dispatch(updateOrder({
                orderUpdataData: updatePoNumber,
                interceptorOption: { skipErrorPopup: true }
              }));

              if (response.payload && response.payload.code === "V3OK" && response.payload.message === "SUCCESS") {
                let successObj: SingleAlertInfo = {
                  message: "Po Number updated successfully",
                  alertType: "success"
                };
                dispatch(setSingleAlertObj(successObj));

                navigate(location.pathname, {
                  state: {
                    ...location.state,
                    orderDetails: {
                      ...order,
                      poNumber: newValue
                    }
                  },
                  replace: true
                });
              }
            } catch (error) {
              console.error("Error updating PO number:", error);
            }
          } else {
            const originalValue = getValues("po") || order?.poNumber || "";
            setPoNumberValue(originalValue);
          }
        }}
        name="po"
      />
    );
  };

  // CS-1251 
  const handleDocumentsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDocumentsMenuAnchor(event.currentTarget);
  };

  const handleDocumentsMenuClose = () => {
    setDocumentsMenuAnchor(null);
  };

  const renderDocumentsMenuButton = () => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Button
        color="info"
        variant="outlined"
        sx={{ whiteSpace: "nowrap", mr: 1 }}
        onClick={handleDocumentsMenuOpen}
        startIcon={<PictureAsPdfOutlined fontSize="small" />}
      >
        Documents
      </Button>
      <Menu
        anchorEl={documentsMenuAnchor}
        open={Boolean(documentsMenuAnchor)}
        onClose={handleDocumentsMenuClose}
      >
        {order?.patientId != null && (
          <MenuItem
            onClick={() => {
              handleCMSPrview(order);
              handleDocumentsMenuClose();
            }}
          >
            <Typography ml={1}>CMS 1500 Form</Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );


  return (
    <Box
      sx={{
        margin: {
          xs: "20px 10px",
          sm: "20px",
        },
      }}
    >
      {/* <Header title="Order Details" /> */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Header title="Claim Details" />
        </Grid>
        <Grid
          item
          xs={12}
          md={9}
          display="flex"
          flexDirection="column"
          sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}
            gap={1}
            pb={1}
          >
            {currClaimId != null && renderDocumentsMenuButton()}
          </Box>
        </Grid>
      </Grid>

      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, productName: "", fileName: "" })
          }
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={orderDocumentInfo?.content || ""}
        />
      )}

      <Box
        sx={{
          border: 1,
          p: 2,
          pt: 4,
          borderRadius: 3,
          borderColor: colors.grey[700],
        }}
      >
        <Grid container spacing={3}>
          <Grid item md={9} xs={12} sx={{ order: { xs: 2, md: 1 } }}>
            <Card sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                {order?.productCategory && order?.productCategory === PRODUCT_CATEGORY.CONSERVATIVE ? (<>
                  <Grid item xs={12}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        color: colors.success[300],
                        // marginBottom: 2,
                        padding: "10px 25px 0px"
                      }}
                    >
                      Details
                    </Typography>

                    {claimedDetailsRows.map((row, index) => (
                      <Box key={index}>
                        <Box sx={{ padding: "2px 25px" }}>
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            sx={{
                              paddingY: 2,
                              flexWrap: {
                                xs: "wrap",
                                sm: "nowrap"
                              },
                              overflowX: "auto",
                            }}
                          >
                            <Grid item md={10} xs={6}>
                              <Typography color={colors.grey[500]}>
                                Description
                              </Typography>
                              <Typography>{row?.productItemDescription}</Typography>
                            </Grid>
                            {row.quantity != null && (
                              <Grid item md={6} xs={3}>
                                <Typography color={colors.grey[500]}>
                                  QTY
                                </Typography>
                                <Typography>
                                  {(row.quantity)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        {index < claimedDetailsRows.length - 1 && (
                          <Box
                            sx={{
                              borderBottom: "1px dashed",
                              borderColor: colors.grey[700],
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Grid>
                </>) : (<>
                  <Grid item xs={12}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        color: colors.success[300],
                        marginBottom: 2,
                        padding: "10px 25px",
                      }}
                    >
                     Details
                    </Typography>

                    {claimedDetailsRows.map((row, index) => (
                      <Box key={index}>
                        <Box sx={{ padding: "2px 25px" }}>
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            sx={{
                              paddingY: 2,
                              flexWrap: {
                                xs: "wrap",
                                sm: "nowrap"
                              },
                              overflowX: "auto",
                            }}
                          >
                            <Grid item md={2} xs={3}>
                              <Typography color={colors.grey[500]}>
                                Ordered Size
                              </Typography>
                              <Typography>{row.orderedGrafts}</Typography>
                            </Grid>
                            <Grid item md={2} xs={5}>
                              <Typography color={colors.grey[500]}>
                                Serial Number
                              </Typography>
                              <Typography>{row.serialNumber|| "-"}</Typography>
                            </Grid>
                            <Grid
                              item
                              md={2}
                              xs={4}
                              sx={{
                                textAlign: {
                                  xs: "left",
                                  sm: "center"
                                },
                              }}
                            >
                              <Typography color={colors.grey[500]}>
                                Status
                              </Typography>
                              <Chip
                                label={row.status}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#2C3E50"
                                      : colors.grey[700],
                                  padding: "1px 10px",
                                  fontWeight: "bold",
                                  border: "1px solid ",
                                  borderColor:
                                    theme.palette.mode === "dark"
                                      ? "#34495E"
                                      : colors.grey[700],
                                }}
                              />
                            </Grid>
                            <Grid item md={1.5} xs={5}>
                              <Typography color={colors.grey[500]}>
                                Date
                              </Typography>
                              <Typography>{row.appRetDate}</Typography>
                            </Grid>
                            <Grid item md={3} xs={7}>
                              <Typography color={colors.grey[500]}>
                                Claim
                              </Typography>
                              <Typography>{row.claimNumber}</Typography>
                            </Grid>
                            {row.quantity != null && (
                              <Grid item md={0.5} xs={2}>
                                <Typography color={colors.grey[500]}>
                                  QTY
                                </Typography>
                                <Typography>
                                  {(row.quantity)}
                                </Typography>
                              </Grid>
                            )}
                            {canViewCOGSPrice &&
                              row.extendedPrice != null &&
                              row.units != null && (
                                <Grid item md={2} sx={{ textAlign: "right" }} xs={row.action ? 8 : 10}>
                                  <Typography color={colors.grey[500]} textAlign="right">
                                    Order Amount
                                  </Typography>
                                  <Typography
                                    sx={{
                                      textDecoration: row.productReturnId && row.productReturnStatus === ORDER_AVAILABILITY.RETURNED ? "line-through red" : "none",
                                      textDecorationThickness: row.productReturnId ? "2px" : "initial",
                                    }}
                                  >
                                    {priceFormatter(row.hasSKUPrice ? row.extendedPrice : row.extendedPrice * row.units)}
                                  </Typography>
                                </Grid>

                              )}

                            <Grid item md={1} xs={2} mb={-1}>
                              {row.action && (
                                <Typography color={colors.grey[500]}>
                                  Action
                                </Typography>
                              )}
                              <Typography>{row.action}</Typography>
                            </Grid>

                          </Grid>
                        </Box>
                        {index < claimedDetailsRows.length - 1 && (
                          <Box
                            sx={{
                              borderBottom: "1px dashed",
                              borderColor: colors.grey[700],
                            }}
                          />
                        )}
                      </Box>
                    ))}

                    {canViewCOGSPrice && (
                      <Box
                        sx={{
                          padding: "10px 25px",
                          marginTop: 2,
                          borderTop: "1px solid",
                          borderColor: colors.grey[700],
                        }}
                      >
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            paddingY: 2,
                            flexWrap: "nowrap",
                            overflowX: "auto",
                          }}
                        >
                          <Grid item md={12} xs={6}>
                            <Typography variant="h6" fontWeight="bold">
                              TOTAL
                            </Typography>
                          </Grid>
                          <Grid item md={2} xs={6} sx={{ textAlign: "right" }}>
                            <Typography variant="h6" fontWeight="bold">
                              {priceFormatter(totalPrice)}
                            </Typography>
                          </Grid>
                          <Grid item md={1}>
                            <Typography></Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Grid>
                </>)}
              </Grid>
            </Card>
            <Card sx={{ padding: 1, mt:2 }}>
              <TimelineView  getValues={getValues} control={control} setValue={setValue} timelineData={timelineClaimData ? timelineClaimData : []} title="Claim Status & Activity Timeline"/>
            </Card>
          </Grid>

          <Grid item md={3} xs={12} sx={{ order: { xs: 1, md: 2 } }}>
            <Card sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box p={3}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ color: colors.success[300], marginBottom: 2 }}
                    >
                      Info
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color={colors.grey[500]}>
                          Patient
                        </Typography>
                        <Typography>
                          {order?.patientName ||
                            order?.orderInfo?.patient ||
                            "-"}
                        </Typography>
                      </Box>
                      {order?.productCategory && order?.productCategory !== PRODUCT_CATEGORY.CONSERVATIVE && (<>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color={colors.grey[500]}>
                            Product
                          </Typography>
                          <Typography>{`${order?.productName} (${order?.manufacturerName})`}</Typography>
                        </Box>
                      </>)}
                      <Box display="flex" justifyContent="space-between">
                        <Typography color={colors.grey[500]}>
                          Provider
                        </Typography>
                        <Typography>
                          {order?.provider || order?.orderInfo?.provider || "-"}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color={colors.grey[500]}>
                          Practice
                        </Typography>
                        <Typography>{order?.practiceName || "-"}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        {/* CS-1141 - Start */}
                        <Typography color={colors.grey[500]}>
                          Order ID
                        </Typography>
                        {/* CS-1141 -End */}
                        <Typography> {order?.orderNumber || "-"}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color={colors.grey[500]}>
                          Claim ID
                        </Typography>
                        <Typography> {order?.claimNumber || "-"}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color={colors.grey[500]}>
                          Date of Service
                        </Typography>
                        <Typography> {order?.dateOfService || "-"}</Typography>
                      </Box>
                      {order?.additionalNotes && (<>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color={colors.grey[500]}>
                            Additional Notes
                          </Typography>
                          {order?.additionalNotes ? (<>
                            <CustomIconButton
                              tooltipPlacement="top"
                              color="info"
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                if (order?.additionalNotes) {
                                  showCustomCommentPopupFromOrderForm(order)
                                } else {

                                }
                              }}
                            >
                              <CommentOutlined fontSize="small" />
                            </CustomIconButton>
                          </>) : (<>-</>)}
                        </Box>
                      </>)}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Grid display="flex" justifyContent="flex-end">
        <Grid item>
          <Button
            color="info"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2, mb: 4 }}
          >
            Back
          </Button>
        </Grid>
      </Grid>
      <CustomCommentPopup
        open={showCommentPopup}
        onClose={hideCustomCommentPopup}
        title={commentData.title}
        commentBody={commentData.body}
      />

      <ShipmentInfoPopup
        open={shipmentInfoOpen}
        onClose={() => setShipmentInfoOpen(false)}
        trackingNumber={order?.trackingNumber || ""}
        orderId={order?.orderId || 0}
        orderNumber={order?.orderNumber || "-"}
        shipper={{
          city: trackingData?.shipping_info?.shipper_address?.city || "",
          state: trackingData?.shipping_info?.shipper_address?.state || "",
          postal_code: trackingData?.shipping_info?.shipper_address?.postal_code || "",
        }}

        recipient={{
          city: trackingData?.shipping_info?.recipient_address?.city || "",
          state: trackingData?.shipping_info?.recipient_address?.state || "",
          postal_code: trackingData?.shipping_info?.recipient_address?.postal_code || "",
        }}
        status={orderTrackingLogs?.status || ""}
        carrierName={orderTrackingLogs?.carrier || ""}
        shippingDate={shippingDate}
        departureTime={departureTime}
        expectedArrival={orderTrackingLogs?.status === "Delivered" ? actualDeliveryTime : expectedArrivalFrom !== "N/A" && expectedArrivalTo !== "N/A" ? expectedArrivalFrom + " - " + expectedArrivalTo : expectedArrivalFrom !== "N/A" ? expectedArrivalFrom : expectedArrivalTo}
        events={scanEvents}
        milestones={dateAndTimes.map((d: any) => d.type)}
      />

    </Box>
  );
};

export default ClaimDetails;
